import React from "react";
import { Info } from "lucide-react";

import { DIAS_SEMANA } from "../regras";

// Peças de base do Relatório de Tempo: seção com título, número grande e seletor.

/** Cartão de uma seção: título com ícone, uma frase do que ela mostra e um canto direito opcional. */
export function Secao({ titulo, icone: Icone, explicacao, direita, children, e2e, className = "" }) {
  return (
    <section className={`bg-card rounded-2xl border p-4 sm:p-5 ${className}`} data-e2e={e2e}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-4">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-bold flex items-center gap-2">
            {Icone && <Icone className="w-5 h-5 text-primary shrink-0" />}
            {titulo}
          </h2>
          {explicacao && <p className="text-xs text-muted-foreground mt-0.5">{explicacao}</p>}
        </div>
        {direita}
      </div>
      {children}
    </section>
  );
}

const COR_VARIACAO = {
  true: "text-emerald-600 dark:text-emerald-400",
  false: "text-red-600 dark:text-red-400",
  null: "text-muted-foreground",
};

/** "▲ 12%" contra o período anterior: verde quando é bom, vermelho quando é ruim, cinza quando é neutro. */
export function Variacao({ v, antes, className = "" }) {
  if (!v) return null;
  const seta = { sobe: "▲", desce: "▼", igual: "=" }[v.sentido];
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold tabular-nums whitespace-nowrap ${COR_VARIACAO[v.bom]} ${className}`}
      title={antes != null ? `No período anterior: ${antes}` : undefined} data-e2e="variacao">
      {seta} {v.texto}
    </span>
  );
}

/**
 * Um número do topo: rótulo, valor e um detalhe embaixo. `destaque` = o número
 * principal da aba. `variacao` (de regras.variacao) + `antes` (o valor anterior já
 * formatado) mostram o ▲▼ contra o período anterior ao lado do número.
 */
export function Kpi({ rotulo, valor, detalhe, icone: Icone, destaque = false, dica, e2e, variacao, antes }) {
  return (
    <div className={`rounded-2xl border p-3.5 sm:p-4 min-w-0 ${destaque ? "bg-primary/10 border-primary/30" : "bg-card"}`}
      title={dica} data-e2e={e2e}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground leading-tight">{rotulo}</p>
        {Icone && <Icone className={`w-4 h-4 shrink-0 ${destaque ? "text-primary" : "text-muted-foreground/70"}`} />}
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 min-w-0">
        <p className={`font-black tabular-nums leading-tight truncate ${destaque ? "text-2xl sm:text-3xl text-primary" : "text-xl sm:text-2xl"}`}>{valor}</p>
        <Variacao v={variacao} antes={antes} />
      </div>
      {detalhe && <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{detalhe}</p>}
    </div>
  );
}

/** Uma linha dizendo contra qual período as setas ▲▼ comparam. */
export function NotaComparacao({ periodo }) {
  if (!periodo?.inicio) return null;
  const fmt = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  const datas = periodo.inicio === periodo.fim ? `o dia ${fmt(periodo.inicio)}` : `${fmt(periodo.inicio)} a ${fmt(periodo.fim)}`;
  const hora = periodo.ate_hora ? `, até as ${periodo.ate_hora.replace(":", "h")} do último dia (o mesmo horário de agora)` : "";
  return (
    <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-e2e="nota-comparacao">
      <span className="font-bold text-foreground/70">▲▼</span>
      <span>As setas comparam com {datas}{hora}. Verde = melhorou, vermelho = piorou.</span>
    </p>
  );
}

/** Botões encostados para escolher uma opção (métrica do gráfico, coluna...). */
export function Segmentado({ opcoes, valor, aoMudar, e2e }) {
  return (
    <div className="inline-flex flex-wrap rounded-lg border bg-muted/40 p-0.5 gap-0.5" role="tablist" data-e2e={e2e}>
      {opcoes.map((o) => (
        <button key={o.key} type="button" role="tab" aria-selected={valor === o.key} onClick={() => aoMudar(o.key)}
          className={`h-7 px-2.5 rounded-md text-xs font-medium transition-colors ${
            valor === o.key ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/**
 * Dias da semana para filtrar um quadro (dono, 27/10/2026: "só sexta e sábado").
 * `valor` = lista de dias marcados (0 = segunda); vazia = todos.
 */
export function FiltroDias({ valor, aoMudar, e2e = "filtro-dias" }) {
  const alternar = (d) => aoMudar(valor.includes(d) ? valor.filter((x) => x !== d) : [...valor, d].sort());
  const botao = (ativo) => `h-7 px-2.5 rounded-md text-xs font-medium transition-colors ${
    ativo ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`;
  return (
    <div className="inline-flex flex-wrap items-center rounded-lg border bg-muted/40 p-0.5 gap-0.5" data-e2e={e2e}>
      <button type="button" className={botao(!valor.length)} onClick={() => aoMudar([])} aria-pressed={!valor.length}>Todos os dias</button>
      {DIAS_SEMANA.map((nome, d) => (
        <button key={nome} type="button" className={botao(valor.includes(d))} onClick={() => alternar(d)} aria-pressed={valor.includes(d)}>{nome}</button>
      ))}
    </div>
  );
}

/** Frase de rodapé que explica um número que gera dúvida. */
export function Nota({ children }) {
  return (
    <p className="flex gap-1.5 text-[11px] text-muted-foreground leading-snug">
      <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
      <span>{children}</span>
    </p>
  );
}

/** Lista de destaques ("o que esse período diz"), um por linha, com ícone. */
export function Destaques({ itens }) {
  const visiveis = (itens || []).filter(Boolean);
  if (!visiveis.length) return null;
  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3" data-e2e="destaques">
      {visiveis.map((d, i) => (
        <div key={i} className="flex items-start gap-2.5 rounded-xl border bg-card px-3 py-2.5">
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${d.cor || "bg-primary/10 text-primary"}`}>
            <d.icone className="w-4 h-4" />
          </span>
          <p className="text-[13px] leading-snug">{d.texto}</p>
        </div>
      ))}
    </div>
  );
}
